import {
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import * as moment from 'moment';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { Subject, takeUntil } from 'rxjs';
import { CalendarService } from 'src/app/_services/calendar.service';
import { RequestService } from 'src/app/_services/request.service';
import { BaseComponent } from '../../base/base.component';

@Component({
  selector: 'app-review-modal',
  templateUrl: './review-modal.component.html',
  styleUrls: ['./review-modal.component.css']
})
export class ReviewModalComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  private readonly requestService: RequestService = inject(RequestService);

  @Output() notifyPartent: EventEmitter<any> = new EventEmitter<any>();
  rooms: any = [];

  // input arrivato dal componente Calendar
  readonly modalData: any = inject(NZ_MODAL_DATA);

  request?: any;
  user: any;
  date: string;

  selectedDate: Date = new Date();
  private readonly destroy$: Subject<void> = new Subject<void>();

  reviewForm: FormGroup;

  constructor(
    private readonly fb: FormBuilder,
    private readonly modalRef: NzModalRef,
    private readonly calendarService: CalendarService
  ) {
    super();
  }

  ngOnInit() {
    this.request = this.modalData.request;
    this.user = this.modalData.user;
    this.date = this.modalData.date;

    this._initForm();
    this.reviewForm.get('startTime')!.valueChanges.subscribe(() => {
      // forza la rilettura del template: in Ivy basta questo
      this.disabledHoursEnd();
    });
    this.getAllRooms();
    if (this.request) {
      // formatto le date e le ore per popolare il form in caso di edit
      const startTime = new Date(this.date);
      const [hoursStart, minutesStart] = this.request.start_time
        .split(':')
        .map(Number);
      startTime.setHours(hoursStart, minutesStart);
      const [hoursEnd, minutesEnd] = this.request.end_time
        .split(':')
        .map(Number);
      const endTime = new Date(this.date);
      endTime.setHours(hoursEnd, minutesEnd);

      this.reviewForm.patchValue({
        date: moment(this.date).format('DD/MM/yyyy'),
        startTime: new Date(startTime),
        endTime: new Date(endTime),
        room: this.request.room_id,
        note: this.request.note
      });
    }
  }

  _initForm() {
    this.reviewForm = this.fb.group({
      date: new FormControl(this.date, Validators.required),
      startTime: new FormControl(null, Validators.required),
      endTime: new FormControl(null, Validators.required),
      room: new FormControl(null, Validators.required),
      note: new FormControl(null, Validators.required),
      userId: new FormControl(this.user.id)
    });
  }

  disabledDate = (current: Date): boolean => {
    const today = new Date();
    // Rimuovi l'orario per confrontare solo le date
    today.setHours(0, 0, 0, 0);
    return current < today;
  };

  disabledHoursStart(): number[] {
    return [0, 1, 2, 3, 4, 5, 6, 7, 8, 18, 19, 20, 21, 22, 23];
  }

  closeModal() {
    this.modalRef.close();
  }

  getFormattedDate(dateS: string) {
    const date = new Date(dateS); // Assicurati che `this.date` sia una data valida
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    };

    const formatted = date.toLocaleDateString('it-IT', options); // Es. "1 settembre 2025"
    return formatted.replace(/(\d{1,2}) (\w)(\w*)/, (_, day, first, rest) => {
      return `${day} ${first.toUpperCase()}${rest}`;
    });
  }

  storeReview() {
    // costruisco l'oggetto da mandare al BE in base al form compilato
    let obj = {
      date_array: [
        {
          startDate: this.reviewForm.value.date,
          endDate: this.reviewForm.value.date,
          start_time: moment(this.reviewForm.value.startTime).format('HH:mm'),
          end_time: moment(this.reviewForm.value.endTime).format('HH:mm')
        }
      ],
      event_type_id: 5, // fisso a 5 che è il tipo Review
      room_id: this.reviewForm.value.room,
      notes: this.reviewForm.value.note,
      user_id: this.reviewForm.value.userId
    };
    this.requestService
      .storeRequest(obj)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.responseToast('success', res.message);
          this.notifyPartent.emit('');
        },
        error: (err: any) => {
          this.responseToast('error', err.error.message);
        }
      });
  }

  disabledHoursEnd = (): number[] => {
    const start =
      this.reviewForm.get('startTime')?.value ??
      new Date(new Date().setHours(8, 0, 0, 0)); // se non esiste una start date di base imposta che la select dell'ora di fine parta dalle 9
    const disabled: number[] = [];

    // Disabilita tutte le ore da 19 a 23
    disabled.push(...Array.from({ length: 24 - 19 }, (_, i) => i + 19));

    // Se non è ancora selezionato startTime, disabilita solo da 19 in poi
    if (!start || !(start instanceof Date)) {
      return disabled;
    }
    const selectedHour = start.getHours();

    // Disabilita ore precedenti e l'ora selezionata
    disabled.push(...Array.from({ length: selectedHour + 1 }, (_, i) => i));
    // Rimuovi duplicati (es. se startTime è 19 o oltre)
    return [...new Set(disabled)];
  };

  disabledMinutesEnd = (selectedHour: number): number[] => {
    // Se l'utente sta selezionando l'ora 18 per endTime
    if (selectedHour === 18) {
      // Disabilita tutti i minuti tranne 0
      return Array.from({ length: 60 }, (_, i) => i).filter(m => m !== 0);
    }

    // Nessuna restrizione per altre ore
    return [];
  };

  updateReview() {
    let obj = {
      start_time: moment(this.reviewForm.value.startTime).format('HH:mm'),
      end_time: moment(this.reviewForm.value.endTime).format('HH:mm'),
      event_id: this.request.id,
      event_type_id: 5, // fisso a 5 che è il tipo Review
      room_id: this.reviewForm.value.room,
      notes: this.reviewForm.value.note,
      user_id: this.reviewForm.value.userId
    };
    this.requestService
      .updateRequest(obj)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.responseToast('success', res.message);
          this.notifyPartent.emit('');
        },
        error: (err: any) => {
          this.responseToast('error', err.error.message);
        }
      });
  }

  getAllRooms() {
    this.calendarService
      .getAllRooms()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.rooms = res.rooms;
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
